import { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

// componentes
import Avatar from "@components/avatar/avatar";
import { SelectPagination } from "@components/select-pagination/select-pagination";
import { Spinner } from "@components/spinner/spinner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@components/tabs";

import { CreateLoadModal } from "@pages/admin/remessas/components/create-load-modal/create-load-modal";
import { MinhasTarefasTable } from "@pages/admin/remessas/components/tabela-minhas-tarefas/tarefas-table";
import { PegarTarefasTable } from "@pages/admin/remessas/components/table-pegar-tarefas/table-pegar-tarefas";

// hooks / react-query
import { usePegarTarefasAdmin } from "@/hooks/admin/pegar-tarefas";

// hooks / zustand
import { useStoreZustandPegarTarefas } from "@/store-zustand/admin/pegar-tarefas";

import ImageAvatar from "@/common/assets/png/perfil-default-letscom.png";

const TAB_QUERY_KEY = "tab";
const TAB_VALUE_MY_TASKS = "my-tasks";

const PegarTarefasAdminPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();

  const currentTab = useMemo(
    () => (searchParams.get(TAB_QUERY_KEY) === TAB_VALUE_MY_TASKS ? "my-tasks" : "all"),
    [searchParams]
  );

  const handleTabChange = useCallback(
    (value: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value === TAB_VALUE_MY_TASKS) {
            next.set(TAB_QUERY_KEY, TAB_VALUE_MY_TASKS);
          } else {
            next.delete(TAB_QUERY_KEY);
          }
          return next;
        },
        { replace: false }
      );
    },
    [setSearchParams]
  );

  const goToMyTasksTab = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set(TAB_QUERY_KEY, TAB_VALUE_MY_TASKS);
        return next;
      },
      { replace: false }
    );
  }, [setSearchParams]);

  const { setItemsPerPage, itemsPerPage } = useStoreZustandPegarTarefas();

  const { downloads: pegasTarefas, total_remessas_solicitadas } = usePegarTarefasAdmin();

  // Função para lidar com a alteração do seletor de itens por página
  const handleItemsPerPageChange = useCallback(
    async (event: React.ChangeEvent<HTMLSelectElement>) => {
      setItemsPerPage(Number(event.target.value));
    },
    [setItemsPerPage]
  );

  return (
    <>
      {isLoading ? (
        <Spinner />
      ) : (
        <div className="flex h-full min-h-0 w-full flex-col overflow-hidden">
          <Tabs
            value={currentTab}
            onValueChange={handleTabChange}
            className="flex min-h-0 w-full flex-1 flex-col"
          >
            <div className="flex shrink-0 items-center">
            <TabsList>
              <TabsTrigger value="all">Remessas</TabsTrigger>
              <TabsTrigger value="my-tasks">Minhas Tarefas</TabsTrigger>
            </TabsList>
            <div className="ml-auto mr-4 flex items-center gap-2">
              <div className="hidden md:block">
                <SelectPagination
                  itemsPerPage={itemsPerPage}
                  handleItemsPerPageChange={handleItemsPerPageChange}
                />
              </div>
            </div>

            <div className="flex justify-end p-4">
              <Avatar alt="Avatar do usuário" size="small" name="avat-perfil" src={ImageAvatar} />
            </div>
          </div>

          {/* Conteúdo da aba "pegar tarefas" */}
          <TabsContent
            value="all"
            className="mt-2 min-h-0 w-full flex-1 overflow-hidden data-[state=inactive]:hidden"
          >
            <PegarTarefasTable
              dadosPegarTarefas={pegasTarefas || []}
              total_remessas_solicitadas={total_remessas_solicitadas}
              setCurrentScreen={goToMyTasksTab}
            />
          </TabsContent>

          {/* Conteúdo da aba "Minhas Tarefas" */}
          <TabsContent
            value="my-tasks"
            className="mt-2 min-h-0 w-full flex-1 overflow-hidden data-[state=inactive]:hidden"
          >
            <MinhasTarefasTable setCurrentScreen={goToMyTasksTab} />
          </TabsContent>

          {isModalOpen && (
            <CreateLoadModal
              handleToggleCreateActvityModal={() => {
                setIsModalOpen(!isModalOpen);
              }}
            />
          )}
          </Tabs>
        </div>
      )}
    </>
  );
};

/**
 * EXPORTS
 */
export { PegarTarefasAdminPage };
